import { useAppDispatch, useAppSelector } from '~/store/hooks'
import type { UtilityBox } from '~/store/objects/types'
import { modelToUI, vectorModelToUI, vectorUIToModel } from '../util/units'
import { Euler, Vector3 } from 'three'
import ObjectClassName from '~/config/objectClassNames'
import { selectObjects } from '~/store/selectedObjects'
import { useWallSegmentEdgeSnapLines } from '../hooks'
import { useState } from 'react'
import { SnapData, snapToLine } from '../util/snaplines'
import { parallelVectorOfWallSegment } from '../util/walls'
import { updateUtilityBox } from '~/store/objects'
import { UtilityBoxConfig } from '~/config/utilityBoxes'
import theme from '~/config/theme'
import { DragControls } from '../util/DragControls'
import LayerKeys from '~/config/layerKeys'
import { handleClick } from '~/components/DrawingCanvas/util/eventHandlers'

export const SNAP_THRESHOLD = 5 * 5

export function UtilityBoxes() {
  const isVisible = useAppSelector(it => it.layers.layers.UTILITY_BOXES.visible)
  const utilityBoxes = useAppSelector(it => it.objects.present.utilityBoxes)

  return (
    <group visible={isVisible} name={LayerKeys.UTILITY_BOXES}>
      {Object.entries(utilityBoxes).map(([key, value]) => (
        <AUtilityBox key={key} utilityBox={value} />
      ))}
    </group>
  )
}

export function SnapPreview({
  snapPoint,
  utilityBox,
  mousePoint,
}: {
  snapPoint: SnapData
  utilityBox: UtilityBoxConfig
  mousePoint: Vector3
}) {
  const dimensions: [number, number, number] = [
    modelToUI(utilityBox.width),
    modelToUI(utilityBox.thickness ?? 24),
    modelToUI(utilityBox.height),
  ]
  const is3D = useAppSelector(it => it.camera.is3D)
  const wallSegment = useAppSelector(it => it.objects.present.segments[snapPoint.objectID])
  const rotation = parallelVectorOfWallSegment(wallSegment)

  const position = snapPoint.distance <= SNAP_THRESHOLD ? snapPoint.snapPoint : mousePoint
  position.setZ(modelToUI(utilityBox.centerPointHeight - utilityBox.height / 2))

  return (
    <>
      <mesh position={position} rotation={rotation}>
        <boxGeometry args={dimensions} />
        <meshBasicMaterial color={utilityBox.color} />
      </mesh>
      {!is3D && (
        <mesh position={position.clone().setZ(modelToUI(wallSegment?.height ?? 48))} rotation={rotation}>
          <boxGeometry args={dimensions} />
          <meshBasicMaterial color={utilityBox.color} />
        </mesh>
      )}
    </>
  )
}

function AUtilityBox({ utilityBox }: { utilityBox: UtilityBox }) {
  const is3D = useAppSelector(it => it.camera.is3D)
  const wallSegment = useAppSelector(it =>
    utilityBox.wallSegmentId ? it.objects.present.segments[utilityBox.wallSegmentId] : undefined
  )
  const dispatch = useAppDispatch()
  const snaplines = useWallSegmentEdgeSnapLines()
  const [dragging, setDragging] = useState(false)
  const [deltaAccumulator, setDeltaAccumulator] = useState<[number, number]>([0, 0])
  const unlocked = useAppSelector(it => !it.layers.layers.UTILITY_BOXES.locked)
  const selected = useAppSelector(it => it.selectedObjects.some(sel => sel.id === utilityBox.id))
  const snapTarget = new Vector3(deltaAccumulator[0], deltaAccumulator[1], 0)
  const snapPoint = dragging ? snapToLine(snaplines, snapTarget) : undefined
  const snapWallSegment = useAppSelector(it =>
    snapPoint?.objectID ? it.objects.present.segments[snapPoint.objectID] : undefined
  )

  const position = vectorModelToUI({ ...utilityBox.position, z: utilityBox.centerPointHeight - utilityBox.height / 2 })

  const dimensions: [number, number, number] = [
    modelToUI(utilityBox.width),
    modelToUI(utilityBox.thickness ?? 24),
    modelToUI(utilityBox.height),
  ]
  const rot = utilityBox.rotation
  const rotation = '_z' in rot ? new Euler(rot._x, rot._y, rot._z) : new Euler(rot.x, rot.y, rot.z)

  const color = selected ? theme.colors.three.objects.utilityBox.selected : utilityBox.color

  return (
    <>
      <DragControls
        dragDisabled={!unlocked}
        onDragStart={() => {
          setDragging(true)
          setDeltaAccumulator([0, 0])
        }}
        onDrag={(_event, delta) => {
          setDeltaAccumulator([delta.x + modelToUI(utilityBox.position.x), delta.y + modelToUI(utilityBox.position.y)])
        }}
        onDragEnd={() => {
          setDragging(false)
          if (snapPoint === undefined) {
            return
          }

          const position = (snapPoint.distance <= SNAP_THRESHOLD ? snapPoint.snapPoint : snapTarget)
            .clone()
            .setZ(modelToUI(wallSegment?.height ?? 48))

          const snapRotation = parallelVectorOfWallSegment(snapWallSegment!)

          const storePosition = vectorUIToModel(position)
          dispatch(
            updateUtilityBox({
              utilityBox: {
                id: utilityBox.id,
                position: { x: storePosition.x, y: storePosition.y, z: storePosition.z },
                wallSegmentId: snapPoint.distance <= SNAP_THRESHOLD ? snapPoint.objectID : undefined,
                ...(snapPoint.distance <= SNAP_THRESHOLD
                  ? { rotation: { x: snapRotation.x, y: snapRotation.y, z: snapRotation.z } }
                  : {}),
              },
            })
          )
        }}
      >
        <group
          visible={!dragging}
          onClick={handleClick(() => {
            dispatch(selectObjects({ objects: [{ id: utilityBox.id, className: ObjectClassName.UTILITY_BOX }] }))
          })}
        >
          <mesh position={position} rotation={rotation}>
            <boxGeometry args={dimensions} />
            <meshBasicMaterial color={color} />
          </mesh>
          {!is3D && (
            <mesh position={position.clone().setZ(modelToUI(wallSegment?.height ?? 48))} rotation={rotation}>
              <boxGeometry args={dimensions} />
              <meshBasicMaterial color={color} />
            </mesh>
          )}
        </group>
      </DragControls>
      {snapPoint && <SnapPreview snapPoint={snapPoint} utilityBox={utilityBox} mousePoint={snapTarget} />}
    </>
  )
}
